/**
 * The strategy badge on a stock row (issue #411): which strategy watches this
 * stock, and whether its fit is due for review (issue #114).
 *
 * "Due" = trading sessions since the fit was last REVIEWED reach the refit
 * cadence (`refitCadenceBars(lookback_days)`, ≥126 — the same rule the morning
 * scan's automatic refit uses, issue #472). Reviewed = the later of `fitted_at`
 * (the fit last changed: a chart save or a server switch) and
 * `refit_checked_at` (the server re-ranked it and kept it). Only a FLAT watch
 * is flagged: the server refits flat watches only.
 *
 * Sessions are counted Mon–Fri with no holiday calendar, so a flag can land a
 * session or two before the server (which counts real bars) acts. Missing
 * inputs never flag.
 */
import { tradingSessionsSince, easternSessionDate } from './earningsElapsed';
import { shortDate } from './dateLabels';
import { rangeLabelFor } from './bollingerChartSettings';
import { fallbackStrategyId } from './strategyTabs';
import { STRATEGY_LABELS } from '../constants/signalStrategies';
import { refitCadenceBars } from '../shared/bandSignals';

const isOpen = (watch) => (watch.position || 'flat') !== 'flat';

// `fitted_at` / `refit_checked_at` are D1's datetime('now'): UTC, a space, no zone — which `new Date`
// would read as LOCAL time. Pin it to UTC, then take the New York session date,
// or an evening save reads as tomorrow in both the hover and the session count.
const sessionDate = (stamp) => {
  if (!stamp) return null;
  const iso = String(stamp).replace(' ', 'T');
  return easternSessionDate(/(Z|[+-]\d{2}:\d{2})$/.test(iso) ? iso : `${iso}Z`);
};
// { fitted, kept } session dates. `kept` is when the server re-ranked the fit and
// KEPT it (refit_checked_at); it only counts when newer than the fit itself.
const reviewDates = (watch) => {
  const fitted = sessionDate(watch?.fitted_at);
  const kept = sessionDate(watch?.refit_checked_at);
  return { fitted, kept: kept && (!fitted || kept > fitted) ? kept : null };
};

// { sessions, cadence, due }, or null when the watch lacks the inputs.
export const fitReview = (watch, now = new Date()) => {
  const lookbackDays = Number(watch?.lookback_days);
  const { fitted, kept } = reviewDates(watch);
  const reviewed = kept || fitted;
  if (!reviewed || !Number.isInteger(lookbackDays) || lookbackDays <= 0) return null;
  const sessions = tradingSessionsSince(reviewed, now);
  if (sessions == null) return null;
  const cadence = refitCadenceBars(lookbackDays);
  return { sessions, cadence, due: !isOpen(watch) && sessions >= cadence };
};

// { name, due, tip } for the watch's badge, or null when there is no watch.
export const strategyBadgeFor = (watch, strategies = [], now = new Date()) => {
  if (!watch) return null;
  const id = watch.strategy_id ?? fallbackStrategyId(strategies);
  const name = strategies.find((s) => s.id === id)?.name || 'Signal watch';
  const review = fitReview(watch, now);
  const due = !!review?.due;

  const details = [`${STRATEGY_LABELS[watch.strategy] || watch.strategy}, ${watch.period}-day`];
  const { fitted, kept } = reviewDates(watch);
  if (fitted) {
    const range = rangeLabelFor(Number(watch.lookback_days));
    details.push(`fitted ${shortDate(fitted)}${range ? ` over ${range}` : ''}`);
  }
  if (kept) details.push(`re-checked ${shortDate(kept)}, kept`);
  if (isOpen(watch)) {
    details.push(`${watch.position}${watch.entry_date ? ` since ${shortDate(watch.entry_date)}` : ''}`);
  }
  let tip = `${name}: ${details.join(' · ')}`;
  if (due) {
    tip += `. Refit due: ${review.sessions} trading days since its last review (every ${review.cadence}). The next morning scan re-ranks it automatically.`;
  }

  return { name, due, tip };
};
